import { PlatformLogo } from '@/lib/performance/platform';
import type { Platform } from '@/lib/performance/types';
import { cn } from '@/lib/utils';

/**
 * The page's one sub-view switcher (ruling 09-08): the Audience tab's
 * segmented control, extracted so every tab's sub-views read identically.
 * Items carry an optional platform mark — set it on single-platform views
 * whenever the surrounding scope mixes platforms.
 */
export function SubViewSwitcher<T extends string>({
  items,
  active,
  onChange,
}: {
  items: { id: T; label: string; platform?: Platform }[];
  active: T;
  onChange: (id: T) => void;
}) {
  return (
    <div role="tablist" className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 bg-gray-50 p-0.5">
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          role="tab"
          aria-selected={item.id === active}
          onClick={() => onChange(item.id)}
          className={cn(
            'h-8 rounded-md px-3 text-sm font-medium transition-colors',
            item.id === active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
          )}
        >
          {item.platform && (
            <PlatformLogo platform={item.platform} className="mr-1.5 inline-block h-3.5 w-3.5 align-text-bottom" />
          )}
          {item.label}
        </button>
      ))}
    </div>
  );
}
